import { Alerts } from "../../../../utils/Alerts.js";
import { RPCDocs } from "../../../../services/rpc.js";
import { useCurrentUser } from "../../../AuthWrapper2/hooks/useCurrentUser.js";
import { useRPCContext } from "../../../../contexts/RPCContext.js";
import { FormikSelectPriority } from "../../../../components/Priority/FormikSelectPriority.js";
import { useEventType } from "../../hooks/useEventType.js";
import { EventTypeTitle } from "../EventTypeTitle.js";
import { EventSelectLocation } from "../../components/EventSelectLocation.js";
import { EventDateTimeField } from "../../components/EventDateTimeField.js";
import { EventTabs } from "./components/EventTabs.js";
import { EventStateField } from "./components/EventStateField.js";
import { EventAssignmentField } from "./components/EventAssignmentField.js";
import { FormikSelectActivityTypeTag } from "../../../Tags/shared/FormikSelectActivityTypeTag.js";
import { useLocationContext } from "../../../Locations/contexts/LocationContext.js";
import { useEffect, useMemo, useState } from "react";
import { Button, FormLayout, Loading, useModalContext } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import { Formik } from "formik";
import * as Yup from "yup";
//#region src/modules/Events/shared/CreateEventForm/index.tsx
var ValidationSchema = Yup.object().shape({
	activityTypeTags: Yup.array(Yup.object()).nullable(),
	assignedUserIds: Yup.array(Yup.string()).required(),
	date: Yup.string().required("Date/Time is required"),
	dispositionFormInput: Yup.object(),
	eventTypeId: Yup.string().required(),
	infoFormInput: Yup.object().nullable(),
	locationId: Yup.string().required("Location is required"),
	priority: Yup.number().required("Priority is required"),
	refsByType: Yup.object({
		eventIds: Yup.array(Yup.string()).nullable(),
		locationIds: Yup.array(Yup.string()).nullable(),
		locationLogEntryIds: Yup.array(Yup.string()).nullable(),
		recordIds: Yup.array(Yup.string()).nullable(),
		reportIds: Yup.array(Yup.string()).nullable(),
		userIds: Yup.array(Yup.string()).nullable()
	}).required(),
	state: Yup.string().oneOf([
		"pending",
		"active",
		"closed"
	]).required()
});
var CreateEventForm = ({ eventTypeId, onComplete }) => {
	const { rpcService } = useRPCContext();
	const { data } = useEventType({ eventTypeId });
	const modalCtx = useModalContext();
	const locationCtx = useLocationContext();
	const currentUser = useCurrentUser();
	const [formBuilderErrors, setFormBuilderErrors] = useState();
	const save = async (values) => {
		try {
			const response = await rpcService.Events.CreateEvent.v1.call({ event: values });
			Alerts.success("Event created");
			onComplete(response.data.eventId);
		} catch (err) {
			console.log("Events.CreateEvent.v1.call() err", err);
			Alerts.error("There was a problem creating the Event", err.message);
		}
	};
	const initialValue = useMemo(() => {
		if (!data?.eventType) return {
			assignedUserIds: [],
			refsByType: {}
		};
		const eventType = data.eventType;
		const formValue = {
			assignedUserIds: [],
			date: (/* @__PURE__ */ new Date()).toISOString(),
			eventTypeId,
			locationId: locationCtx?.locationId,
			priority: data?.eventType?.handlingConfig?.defaultPriority,
			refsByType: {},
			state: RPCDocs.Events.CreateEvent.v1.State.Pending
		};
		if (eventType.handlingConfig.autoAssignToAuthor) formValue.assignedUserIds?.push(currentUser.id);
		return formValue;
	}, [data?.eventType]);
	return /* @__PURE__ */ jsx(Formik, {
		initialValues: initialValue,
		onSubmit: save,
		enableReinitialize: true,
		validationSchema: ValidationSchema,
		validateOnChange: true,
		children: ({ isSubmitting, setFieldValue, submitForm, touched }) => {
			const title = useMemo(() => {
				if (!data?.eventType) return null;
				return /* @__PURE__ */ jsxs("div", {
					className: "flex w-full grow items-center justify-between",
					children: [/* @__PURE__ */ jsx("div", {
						className: "text-lg",
						children: data && /* @__PURE__ */ jsx(EventTypeTitle, { eventType: data.eventType })
					}), /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Button, {
						disabled: isSubmitting || !!formBuilderErrors,
						onClick: submitForm,
						variant: "main",
						children: "Create Event"
					}) })]
				});
			}, [data?.eventType, formBuilderErrors]);
			useEffect(() => {
				if (modalCtx?.setHeader && title) modalCtx.setHeader(title);
			}, [
				modalCtx,
				title,
				formBuilderErrors
			]);
			return /* @__PURE__ */ jsxs(FormLayout, { children: [
				!data && /* @__PURE__ */ jsx(Loading, {
					message: "Loading Event Type",
					overlay: true
				}),
				!modalCtx?.setHeader && title,
				/* @__PURE__ */ jsx(EventSelectLocation, {}),
				/* @__PURE__ */ jsx(EventDateTimeField, {}),
				/* @__PURE__ */ jsx(FormikSelectActivityTypeTag, {
					label: "Activity Types",
					multi: true,
					name: "activityTypeTags",
					onChangeOptions: (options) => {
						if (!touched.priority) setFieldValue("priority", Math.max(...options.map((tag) => tag.level || 0)));
					}
				}),
				/* @__PURE__ */ jsx(FormikSelectPriority, {
					label: "Priority",
					name: "priority"
				}),
				/* @__PURE__ */ jsx(EventStateField, {}),
				/* @__PURE__ */ jsx(EventAssignmentField, {}),
				data && /* @__PURE__ */ jsx(EventTabs, {
					eventType: data?.eventType,
					onFormErrorStateChange: setFormBuilderErrors
				}),
				modalCtx?.setHeader && /* @__PURE__ */ jsx(FormLayout.Controls, {
					submitLabel: "Create Event",
					disabled: isSubmitting || !!formBuilderErrors,
					onSubmit: submitForm
				})
			] });
		}
	});
};
//#endregion
export { CreateEventForm };

//# sourceMappingURL=index.js.map